<template>
  <div class="delegation-restriction d-flex flex-column ga-5">
    <section class="d-flex flex-column ga-3">
      <div class="d-flex align-center ga-3">
        <v-avatar color="blue-darken-3" size="22">
          <span class="text-white font-weight-bold">1</span>
        </v-avatar>
        <span class="font-weight-bold">Оберіть тип документа</span>
      </div>

      <div class="d-grid select-grid ga-3">
        <b-select
          v-model="credentials.docTypeClassId"
          :error-messages="getErrorMessages(v$.docTypeClassId)"
          :items="docTypeClasses"
          :loading="$loading.isLoading('docTypeClasses')"
          label="Клас документа"
          @blur="v$.docTypeClassId.$touch()"
          @update:model-value="onDocTypeClassChange" />

        <b-select
          v-model="credentials.docTypeDefinitionId"
          :disabled="!credentials.docTypeClassId"
          :error-messages="getErrorMessages(v$.docTypeDefinitionId)"
          :items="filteredDocTypeDefinitions"
          :loading="$loading.isLoading('docTypeDefinitions')"
          label="Тип документа"
          @blur="v$.docTypeDefinitionId.$touch()" />
      </div>
    </section>

    <section
      :class="{ 'restriction-section-disabled': isRestrictionSectionDisabled }"
      class="d-flex flex-column ga-3 restriction-section">
      <div class="d-flex align-center justify-space-between ga-3">
        <div class="d-flex align-center ga-3">
          <v-avatar color="blue-darken-3" size="22">
            <span class="text-white font-weight-bold">2</span>
          </v-avatar>
          <span class="font-weight-bold">Вкажіть обмеження</span>
        </div>
        <span
          class="text-disabled font-size-12 font-weight-bold text-uppercase">
          Оберіть принаймні одне
        </span>
      </div>

      <div class="d-flex flex-column ga-2">
        <v-card
          v-for="restriction in restrictionTypes"
          :key="restriction.id"
          :class="{
            selected:
              isSelected(restriction.id) ||
              isGloballyRestricted(restriction.id),
            'globally-restricted': isGloballyRestricted(restriction.id),
          }"
          :style="restriction.style"
          border
          class="restriction-option"
          rounded="lg"
          @click="toggleRestriction(restriction.id)">
          <v-card-text
            class="d-flex align-center justify-space-between ga-3 pa-3">
            <div class="d-flex align-center ga-3">
              <v-checkbox
                :color="restriction.color"
                :disabled="
                  isRestrictionSectionDisabled ||
                  isGloballyRestricted(restriction.id)
                "
                :model-value="
                  isSelected(restriction.id) ||
                  isGloballyRestricted(restriction.id)
                "
                density="compact"
                hide-details
                @click.stop
                @update:model-value="toggleRestriction(restriction.id)" />
              <div class="d-flex flex-column">
                <span class="font-weight-medium">
                  {{ restriction.name }}
                </span>
                <span
                  v-if="isGloballyRestricted(restriction.id)"
                  class="font-size-12"
                  style="color: #d32f2f">
                  Вже заборонено глобально
                </span>
                <span v-else class="text-disabled font-size-12">
                  Повідомлення: «Ви не маєте прав на виконання цієї дії...»
                </span>
              </div>
            </div>

            <v-chip
              v-if="isGloballyRestricted(restriction.id)"
              color="error"
              size="small"
              variant="tonal">
              <v-icon class="mr-1" size="small">mdi-earth</v-icon>
              <span class="font-weight-bold text-uppercase">Глобально</span>
            </v-chip>
            <v-chip
              v-else
              :color="restriction.color"
              size="small"
              variant="tonal">
              <span class="font-weight-bold text-uppercase">
                {{ restriction.shortName }}
              </span>
            </v-chip>
          </v-card-text>
        </v-card>
      </div>

      <div
        v-if="v$.restrictionTypeIds.$error"
        class="text-error font-size-12 mt-n1">
        Оберіть принаймні одне обмеження
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, onMounted, reactive, shallowRef } from 'vue'
import { minLength, required, useVuelidate } from '@/plugins'
import { getErrorMessages } from 'best-modules/utils'
import { fillDirectory, getDirectory } from '@/directory'

type DirectoryItem = {
  id: number
  name: string
  docTypeClassId?: number
  doc_type_class_id?: number
  docTypeClass?: {
    id: number
  }
}

type RestrictionItem = DirectoryItem & {
  color: string
  shortName: string
  style: string
}

export default {
  name: 'DelegationRestriction',
  props: {
    dialog: {
      type: Object,
      required: true,
    },
  },
  methods: { getErrorMessages },
  setup(props) {
    const sourceRestriction = props.dialog.dialogData.restriction || {}
    const globallyRestrictedTypeIds: number[] =
      props.dialog.dialogData.globallyRestrictedTypeIds || []

    const credentials = reactive({
      docTypeClassId: sourceRestriction.docTypeClassId || null,
      docTypeDefinitionId: sourceRestriction.docTypeDefinitionId || null,
      restrictionTypeIds: sourceRestriction.restrictionTypeIds || [],
    })

    const docTypeClasses = shallowRef<DirectoryItem[]>([])
    const docTypeDefinitions = shallowRef<DirectoryItem[]>([])
    const filteredDocTypeDefinitions = shallowRef<DirectoryItem[]>([])
    const restrictionTypes = shallowRef<RestrictionItem[]>([])

    const isRestrictionSectionDisabled = computed(() => {
      return !credentials.docTypeClassId || !credentials.docTypeDefinitionId
    })

    const rules = {
      docTypeClassId: { required },
      docTypeDefinitionId: { required },
      restrictionTypeIds: { required, minLength: minLength(1) },
    }

    const v$ = useVuelidate(rules, credentials)

    const syncDirectories = () => {
      docTypeClasses.value = getDirectory('docTypeClasses')
      docTypeDefinitions.value = getDirectory('docTypeDefinitions')
      restrictionTypes.value = getDirectory('delegationRestrictionTypes').map(
        item => {
          const color = getRestrictionColor(item.id)

          return {
            ...item,
            color,
            shortName: getRestrictionShortName(item.name),
            style: `--restriction-border-color: ${getRestrictionBorderColor(
              item.id
            )}`,
          }
        }
      )
    }

    const setFilteredDocTypeDefinitions = (docTypeClassId: number | null) => {
      if (!docTypeClassId) {
        filteredDocTypeDefinitions.value = []
        return
      }

      filteredDocTypeDefinitions.value = docTypeDefinitions.value.filter(
        item => {
          return (
            item.docTypeClassId === docTypeClassId ||
            item.doc_type_class_id === docTypeClassId ||
            item.docTypeClass?.id === docTypeClassId
          )
        }
      )
    }

    const onDocTypeClassChange = (docTypeClassId: number | null) => {
      credentials.docTypeDefinitionId = null
      setFilteredDocTypeDefinitions(docTypeClassId)
      v$.value.docTypeDefinitionId.$reset()
    }

    const isSelected = (restrictionTypeId: number) => {
      return credentials.restrictionTypeIds.includes(restrictionTypeId)
    }

    const isGloballyRestricted = (restrictionTypeId: number) => {
      return globallyRestrictedTypeIds.includes(restrictionTypeId)
    }

    const toggleRestriction = (restrictionTypeId: number) => {
      if (isRestrictionSectionDisabled.value) return
      if (isGloballyRestricted(restrictionTypeId)) return

      const ids = credentials.restrictionTypeIds

      if (ids.includes(restrictionTypeId)) {
        credentials.restrictionTypeIds = ids.filter(
          (id: any) => id !== restrictionTypeId
        )
      } else {
        credentials.restrictionTypeIds = [...ids, restrictionTypeId]
      }

      v$.value.restrictionTypeIds.$touch()
    }

    const getRestrictionShortName = (name: string) => {
      return name.replace('Заборона ', '').replace(' документів', '')
    }

    const getRestrictionColor = (id: number) => {
      return (
        {
          1: 'blue-darken-3',
          2: 'green-darken-2',
          3: 'orange-darken-2',
          4: 'red-darken-2',
          5: 'purple-darken-2',
        }[id] || 'grey'
      )
    }

    const getRestrictionBorderColor = (id: number) => {
      return (
        {
          1: '#1565c0',
          2: '#388e3c',
          3: '#f57c00',
          4: '#d32f2f',
          5: '#7b1fa2',
        }[id] || '#9E9E9E'
      )
    }

    const submit = () => {
      return Promise.resolve({
        docTypeClassId: credentials.docTypeClassId,
        docTypeDefinitionId: credentials.docTypeDefinitionId,
        restrictionTypeIds: credentials.restrictionTypeIds,
      })
    }

    onMounted(async () => {
      await fillDirectory([
        'docTypeClasses',
        'docTypeDefinitions',
        'delegationRestrictionTypes',
      ])
      syncDirectories()

      if (credentials.docTypeDefinitionId && !credentials.docTypeClassId) {
        const definition = docTypeDefinitions.value.find(
          d => d.id === credentials.docTypeDefinitionId
        )
        if (definition) {
          credentials.docTypeClassId =
            definition.docTypeClassId ?? definition.doc_type_class_id ?? null
        }
      }

      setFilteredDocTypeDefinitions(credentials.docTypeClassId)
    })

    return {
      credentials,
      docTypeClasses,
      filteredDocTypeDefinitions,
      restrictionTypes,
      isRestrictionSectionDisabled,
      submit,
      v$,
      onDocTypeClassChange,
      isSelected,
      isGloballyRestricted,
      toggleRestriction,
    }
  },
}
</script>

<style lang="scss" scoped>
.select-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 720px) {
  .select-grid {
    grid-template-columns: 1fr;
  }
}

.restriction-option {
  background: #ffffff;
  cursor: pointer;

  &.selected {
    background: #ffffff;
    border-color: var(--restriction-border-color);
  }

  &.globally-restricted {
    background: #fff5f5;
    border-color: #ef9a9a !important;
    cursor: default;
    opacity: 0.85;
  }
}

.restriction-section-disabled {
  opacity: 0.55;

  .restriction-option {
    background: #ffffff;
    cursor: default;
  }
}
</style>
